import React, { useEffect, useState } from 'react';
import { NameEmailAvatar } from '../../../../../ui/components/Common/NameEmailAvatar';
import { ClipBoardIcon, ClockIcon, SolidCheckIcon, TrashIcon } from '../../../../../ui/icons/icons';
import { Modal } from '../../../../../ui/modal/modal';
import { Applicant } from '../../../../../utils/types/JobOpenings';

interface UnsuccessfulEmailConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedApplicants: Applicant[];
  loading?: boolean;
  onRemoveApplicant: (userId: string) => void;
  onClearAll?: () => void;
}

export const UnsuccessfulEmailConfirmationModal: React.FC<UnsuccessfulEmailConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedApplicants,
  loading = false,
  onRemoveApplicant,
  onClearAll,
}) => {
  const [confirmationChecked, setConfirmationChecked] = useState(false);

  // Reset checkbox when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmationChecked(false);
    }
  }, [isOpen]);

  // Filter to only show unsuccessful-pending applicants
  const pendingApplicants = selectedApplicants.filter(
    (applicant) => applicant.applicantStatus === 'Unsuccessful-Pending' && applicant.email,
  );

  const isLargeBatch = pendingApplicants.length > 50;

  const modalContent = () => (
    <div className='relative bg-white shadow-xl max-w-4xl mx-auto overflow-hidden'>
      { /* Header section with gradient */ }
      <div className='relative bg-gradient-to-r from-red-500 to-red-600 px-6 py-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='w-8 h-8 bg-white/20 backdrop-blur-sm flex items-center justify-center'>
              <svg
                  className='w-5 h-5 text-white'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                />
              </svg>
            </div>
            <div>
              <h3 className='text-lg font-bold text-white'>
                Email Confirmation Required
              </h3>
              <p className='text-red-100 text-sm'>
                Review candidates before sending unsuccessful emails
              </p>
            </div>
          </div>
          <button
              onClick={onClose}
              disabled={loading}
              className='w-6 h-6 bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors flex items-center justify-center text-white disabled:opacity-50'
          >
            <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>
      </div>

      <div className='px-6 py-4'>
        <div className='space-y-4'>
          { /* Summary card */ }
          { isLargeBatch && (
            <div className='bg-amber-50 border-l-2 border-amber-400 p-3'>
              <div className='flex items-start gap-3'>
                <div className='w-6 h-6 bg-amber-100 flex items-center justify-center flex-shrink-0'>
                  <svg
                      className='w-4 h-4 text-amber-600'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                  >
                    <path
                        fillRule='evenodd'
                        d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                        clipRule='evenodd'
                    />
                  </svg>
                </div>
                <div className='flex-1'>
                  <h4 className='text-sm font-bold text-amber-800 mb-1 flex items-center gap-1'>
                    <svg
                        className='w-4 h-4'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M8 9l3 3-3 3m13 0H3'
                      />
                    </svg>
                    <span>
                      Large Batch ({ pendingApplicants.length } candidates)
                    </span>
                  </h4>
                  <p className='text-xs text-amber-700'>
                    Processing may take several minutes. Please keep this window
                    open.
                  </p>
                </div>
              </div>
            </div>
          ) }
        </div>
      </div>

      { pendingApplicants.length === 0 ? (
        <div className='px-6 pb-4'>
          <div className='text-center py-8 bg-gray-50'>
            <div className='w-12 h-12 bg-gray-100 flex items-center justify-center mx-auto mb-3'>
              <svg
                  className='w-6 h-6 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2a2 2 0 012 2v1M4 13v6a2 2 0 002 2h4'
                />
              </svg>
            </div>
            <h3 className='text-base font-medium text-gray-900 mb-1'>
              No Eligible Candidates
            </h3>
            <p className='text-sm text-gray-600 max-w-sm mx-auto'>
              No unsuccessful-pending candidates with email addresses found in
              your selection.
            </p>
          </div>
        </div>
      ) : (
        <div className='px-6'>
          <div className='mb-4'>
            <div className='flex items-center justify-between mb-3'>
              <h4 className='text-base font-semibold text-gray-900 flex items-center gap-2'>
                <ClipBoardIcon className='w-4 h-4' />
                <span>Selected Candidates</span>
                <span className='inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800'>
                  { pendingApplicants.length } pending
                </span>
              </h4>
              { pendingApplicants.length > 0 && (
                <button
                    onClick={onClearAll}
                    className='text-sm text-red-600 hover:text-red-800 underline font-medium transition-colors'
                    disabled={loading}
                >
                  Clear All
                </button>
              ) }
            </div>
            <div className='max-h-60 overflow-y-auto space-y-2'>
              { pendingApplicants.map((applicant, index) => (
                <div
                    key={applicant.userId}
                    className='group relative p-3 hover:shadow-sm hover:border-blue-300 transition-all duration-200'
                >
                  <div className='flex items-center gap-3'>
                    <div className='flex w-full items-start justify-between'>
                      <NameEmailAvatar
                          name={applicant.name}
                          email={applicant.email}
                          avatar=''
                      />
                      <div className='ml-3 flex items-center gap-2'>
                        <span className='inline-flex items-center px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800'>
                          <span className='w-2 h-2 bg-yellow-400 mr-1'></span>
                          { applicant.applicantStatus }
                        </span>

                        <button
                            onClick={() =>
                              onRemoveApplicant(applicant.userId)
                            }
                            className='p-1 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100'
                            disabled={loading}
                            title='Remove candidate'
                        >
                          <TrashIcon className='w-4 h-4' />
                        </button>

                      </div>
                    </div>
                  </div>
                </div>
              )) }
            </div>
          </div>

          { /* Compact confirmation section */ }
          <div className='bg-gray-50 p-4'>
            <div className='flex items-start gap-3'>
              <div className='relative flex-shrink-0 pt-1'>
                <label className='relative inline-flex items-center cursor-pointer'>
                  <input
                      type='checkbox'
                      checked={confirmationChecked}
                      onChange={(e) => setConfirmationChecked(e.target.checked)}
                      disabled={loading}
                      className='sr-only peer'
                  />
                  <div className='relative w-5 h-5 bg-white border-2 border-gray-300 peer-focus:ring-2 peer-focus:ring-red-300 peer-checked:bg-red-600 peer-checked:border-red-600 transition-all duration-200 disabled:opacity-50'>
                    <svg
                        className='absolute inset-0.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-200'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                    >
                      <path
                          fillRule='evenodd'
                          d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                          clipRule='evenodd'
                      />
                    </svg>
                  </div>
                </label>
              </div>
              <div className='flex-1'>
                <h4 className='text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2'>
                  <span>I understand this action cannot be undone</span>
                </h4>
                <p className='text-xs text-gray-600'>
                  I confirm that I want to send unsuccessful emails to
                  <span className='font-semibold text-gray-900'>
                    { ' ' }
                    { pendingApplicants.length } candidate
                    { pendingApplicants.length !== 1 ? 's' : '' }
                  </span>
                  { '' } and permanently change their status to
                  &quot;Unsuccessful-Closed&quot;.
                  { isLargeBatch && (
                    <span className='block mt-1 text-amber-600 font-medium flex items-center gap-1'>
                      <ClockIcon className='w-3 h-3' />
                      <span>
                        This large batch may take several minutes to process.
                      </span>
                    </span>
                  ) }
                </p>
              </div>
            </div>
          </div>
        </div>
      ) }

      { /* Action buttons */ }
      <div className='px-6 py-4 bg-gray-50 border-t border-gray-200'>
        <div className='flex items-center justify-between'>
          <div className='text-xs text-gray-500'>
            { pendingApplicants.length > 0 && (
              <span>
                Ready to send to { pendingApplicants.length } candidate
                { pendingApplicants.length !== 1 ? 's' : '' }
              </span>
            ) }
          </div>
          <div className='flex gap-3'>
            <button
                onClick={onClose}
                disabled={loading}
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <button
                onClick={onConfirm}
                disabled={
                  loading ||
                pendingApplicants.length === 0 ||
                !confirmationChecked
                }
                className='group relative px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md'
            >
              { loading ? (
                <div className='flex items-center gap-2'>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent animate-spin'></div>
                  <span>
                    { isLargeBatch
                      ? `Processing ${pendingApplicants.length} emails...`
                      : 'Sending emails...' }
                  </span>
                </div>
              ) : (
                <div className='flex items-center gap-2'>
                  <svg
                      className='w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                    />
                  </svg>
                  <span>Send Emails ({ pendingApplicants.length })</span>
                </div>
              ) }
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex min-h-screen items-center justify-center px-4'>
        <div className='fixed inset-0 bg-black opacity-50' onClick={onClose}></div>
        <div className='relative w-full max-w-4xl'>
          { modalContent() }
        </div>
      </div>
    </div>
  );
};
